import {
  Card,
  CardContent,
  CardActions,
  Typography,
  Button,
  Chip,
  Box,
  Stack,
  Tooltip,
  CircularProgress,
  Switch,
  FormControlLabel,
} from "@mui/material";
import { useContext, useEffect, useMemo, useState } from "react";
import { LanguageContext } from "../../context/LanguageContext";
import EditIcon from "@mui/icons-material/Edit";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import { doc, updateDoc, collection, onSnapshot } from "firebase/firestore";
import { db } from "../../firebase";
import { todayKey } from "../dateKeys";

export default function AgentCard({
  agent,
  compact = false,
  onEdit,
  tenantId,
}) {
  const { translate } = useContext(LanguageContext);
  const [totalInteractions, setTotalInteractions] = useState(
    typeof agent?.totalInteractions === "number" ? agent.totalInteractions : 0
  );
  const [monthlyTokens, setMonthlyTokens] = useState(0);
  const [monthlyCostUsd, setMonthlyCostUsd] = useState(0);
  const [convTotal, setConvTotal] = useState(0);

  const monthPrefix = useMemo(() => todayKey().slice(0, 7), []);

  const togglePublic = async (checked) => {
    if (!tenantId || !agent?.id) return;
    try {
      const ref = doc(db, "tenants", tenantId, "agents", agent.id);
      await updateDoc(ref, { publicEnabled: checked });
    } catch (e) {
      console.error("[AgentCard] toggle publicEnabled failed", e);
    }
  };

  // Conversations + interactions
  useEffect(() => {
    if (!agent?.id) return;
    try {
      const ref = collection(
        db,
        "messages",
        `agent_${agent.id}`,
        "conversations"
      );
      const unsub = onSnapshot(ref, (snap) => {
        let sum = 0;
        let count = 0;
        snap.forEach((d) => {
          const it = d.data()?.interactions || 0;
          sum += typeof it === "number" ? it : 0;
          count += 1;
        });
        setTotalInteractions(sum);
        setConvTotal(count);
      });
      return unsub;
    } catch {}
  }, [agent?.id]);

  // Monthly usage from usage_daily
  useEffect(() => {
    if (!tenantId || !agent?.id) return;
    const usageRef = collection(
      db,
      "tenants",
      tenantId,
      "agents",
      agent.id,
      "usage_daily"
    );
    try {
      const unsub = onSnapshot(usageRef, (snap) => {
        let tokens = 0;
        let cost = 0;
        snap.forEach((docSnap) => {
          const id = docSnap.id || "";
          const data = docSnap.data() || {};
          if (id.startsWith(monthPrefix)) {
            tokens += Number(data.totalTokens || 0) || 0;
            cost += Number(data.costUsd || 0) || 0;
          }
        });
        setMonthlyTokens(tokens);
        setMonthlyCostUsd(cost);
      });
      return unsub;
    } catch {}
  }, [tenantId, agent?.id, monthPrefix]);

  const monthlyBudgetUsd = 1;
  const pct = Math.max(
    0,
    Math.min(100, (monthlyCostUsd / monthlyBudgetUsd) * 100)
  );
  const hue = Math.max(0, 140 - pct * 1.4);
  // const gaugeColor = `hsl(${hue}, 80%, 45%)`;
  const gaugeColor = "#00E676";

  return (
    <Card
      sx={{
        bgcolor: "background.paper",
        color: "text.primary",
        borderRadius: 3,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        width: 320,
        border: 1,
        borderColor: "divider",
        transition: "transform .15s ease, box-shadow .15s ease",
        "&:hover": { transform: "translateY(-2px)", boxShadow: 6 },
      }}
      elevation={0}
    >
      <CardContent sx={{ flexGrow: 1 }}>
        <Stack
          direction="row"
          alignItems="center" // centrage vertical
          justifyContent="space-between" // texte à gauche, chip à droite
          sx={{
            mb: 1,
            width: "100%",
            maxWidth: "100%",
          }}
        >
          <Tooltip title={agent?.name || "Agent"}>
            <Typography
              variant={compact ? "h7" : "h6"}
              fontWeight={600}
              noWrap
              sx={{
                letterSpacing: 0.2,
                m: 0,
                textAlign: "left",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                flex: 1, // prend tout l’espace restant
                cursor: "default",
                pr: 2, // petit espace avant le chip
              }}
            >
              {agent?.name || "Agent"}
            </Typography>
          </Tooltip>

          <Tooltip
            title={
              agent?.publicEnabled
                ? translate("ClickToDeactivate") || "Click to deactivate"
                : translate("ClickToActivate") || "Click to activate"
            }
            arrow
          >
            <Chip
              size="small"
              clickable
              onClick={() => togglePublic(!(agent?.publicEnabled === true))}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  togglePublic(!(agent?.publicEnabled === true));
                }
              }}
              label={
                agent?.publicEnabled === false
                  ? translate("Inactive") || "Inactive"
                  : translate("Active") || "Active"
              }
              role="switch"
              aria-checked={agent?.publicEnabled === true}
              sx={{
                backgroundColor:
                  agent?.publicEnabled === false ? "#3B0A0A" : "#003B2E",
                color: agent?.publicEnabled === false ? "#FF5252" : "#00E676",
                fontWeight: 600,
                border: "none",
                px: 1.4,
                letterSpacing: 0.3,
                flexShrink: 0,
                cursor: "pointer",
                transition: "all .2s ease",
                "&:hover": { filter: "brightness(1.1)" },
                "&:active": { transform: "scale(0.98)" },
                "&:focus-visible": {
                  outline: "2px solid rgba(0,230,118,0.6)",
                  outlineOffset: 2,
                },
              }}
            />
          </Tooltip>
        </Stack>

        <Stack
          direction="row"
          spacing={1}
          flexWrap="wrap"
          alignItems="center"
          sx={{ mb: 1, gap: 2 }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              width: "100%",
              maxWidth: "100%",
              paddingTop: "20px",
            }}
          >
            <div style={{ display: "flex", flexDirection: "column" }}>
              <div style={{ color: "lightgray" }}>{`${
                translate("Conversations") || "Conversations"
              }           
          `}</div>
              <div style={{ color: "white", fontSize: "42px" }}>{`           
          ${convTotal}`}</div>
            </div>

            <div style={{ display: "flex", flexDirection: "column" }}>
              <div style={{ color: "lightgray" }}>{`${
                translate("Interactions") || "Interactions"
              }           
          `}</div>
              <div style={{ color: "white", fontSize: "42px" }}>{`           
         ${totalInteractions}`}</div>
            </div>
          </div>
        </Stack>

        <Box sx={{ mt: 1.5, display: "flex", alignItems: "center", gap: 2 }}>
          <Box sx={{ position: "relative", display: "inline-flex" }}>
            <CircularProgress
              variant="determinate"
              value={100}
              size={86}
              thickness={5}
              sx={{ color: "action.disabledBackground" }}
            />
            <CircularProgress
              variant="determinate"
              value={pct}
              size={86}
              thickness={5}
              sx={{
                position: "absolute",
                left: 0,
                color: gaugeColor,
                filter: "drop-shadow(0 1px 4px rgba(0,0,0,0.25))",
              }}
            />
            <Box
              sx={{
                top: 0,
                left: 0,
                bottom: 0,
                right: 0,
                position: "absolute",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexDirection: "column",
              }}
            >
              <Typography variant="subtitle2" fontWeight={700} fontSize={18}>
                {Math.round(pct)}%
              </Typography>
              {/* <Typography variant="caption" color="text.secondary">
                of $5
              </Typography> */}
            </Box>
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="caption" color="text.secondary">
              {translate("ThisMonth") || "This month"}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                display: "flex",
                alignItems: "baseline",
                gap: 1,
              }}
            >
              {new Intl.NumberFormat().format(Math.round(monthlyTokens))} tokens
              <Typography
                component="span"
                variant="caption"
                sx={{ color: "text.disabled", ml: 0.5 }}
              >
                {pct.toFixed(5)}%
              </Typography>
            </Typography>
          </Box>
        </Box>
      </CardContent>

      <CardActions
        sx={{
          p: 2,
          pt: 0,
          justifyContent: "flex-end",
        }}
      >
        <Tooltip title={translate("Edit") || "Edit"} arrow>
          <Button
            size="small"
            variant="contained"
            startIcon={<EditIcon />}
            onClick={() => onEdit?.(agent)}
            sx={{
              backgroundColor: "#2B2B2B", // gris foncé (comme sur l'image)
              color: "#FFFFFF", // texte blanc
              fontWeight: 600,
              textTransform: "none",
              borderRadius: "8px",
              px: 2.4,
              boxShadow: "none",
              transition: "all 0.2s ease",
              "&:hover": {
                backgroundColor: "#3A3A3A", // légèrement plus clair au survol
                boxShadow: "0 0 6px rgba(255,255,255,0.1)",
              },
              "&:active": {
                transform: "scale(0.97)",
              },
            }}
          >
            {translate("Edit") || "Edit"}
          </Button>
        </Tooltip>
      </CardActions>
    </Card>
  );
}
